import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ConfirmDialogComponent } from './../../../shared/components/confirm-dialog/confirm-dialog.component';
import { NotificationServiceService } from './../../../../../services/notification-service/notification-service.service';
import { AuthService } from './../../../../../services/API/auth/auth.service';
import { MatSort } from '@angular/material/sort';
import { ScalesService } from './../../../../../services/API/scales/scales.service';
import { NewScaleComponent } from './components/new-scale/new-scale.component';
import { EditScaleComponent } from './components/edit-scale/edit-scale.component';
import { io, Socket } from 'socket.io-client';
import { AuxiliarService } from './../../../../../services/auxiliar.service';

type ConnectionState = 'pending' | 'online' | 'offline';

@Component({
  selector: 'app-balanzas',
  templateUrl: './balanzas.component.html',
  styleUrls: ['./balanzas.component.css'],
  standalone: false
})
export class BalanzasComponent implements OnInit, OnDestroy {
  showProgressBar: boolean = true;
  displayedColumns = ['id', 'name', 'description', 'default_camion', 'default_material', 'connection', 'active'];
  dataSource1: any = [];
  pages = [20, 50, 100];
  filter: string;
  dataSource = new MatTableDataSource(this.dataSource1);
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('TABLE') table: ElementRef;
  @ViewChild(MatSort) sort: MatSort;
  estado: string = 'true';
  connectionByScale: Record<string, ConnectionState> = {};
  checkingConnections = false;

  private socket: Socket | null = null;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private dialog: MatDialog,
    public authService: AuthService,
    private scalesService: ScalesService,
    private notificationService: NotificationServiceService,
    private auxiliarService: AuxiliarService,
  ) { }

  ngOnInit(): void {
    this.userRolCheck('permiso_total');
    this.setupSocket();
    this.getScales();
    this.refreshTimer = setInterval(() => this.checkAllConnections(), 30000);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  userRolCheck(permission: string) {
    if (!this.authService.userRolCheck(permission)) {
      this.authService.logout();
    }
  }

  setupSocket(): void {
    this.socket = io(this.auxiliarService.getSocket(), {
      transports: ['polling', 'websocket'],
      reconnection: true,
    });
    this.socket.on('connect', () => this.checkAllConnections());
  }

  getScales() {
    this.showProgressBar = true;
    this.scalesService.getScales(this.estado).subscribe(result => {
      this.showProgressBar = false;
      this.dataSource1 = result;
      this.prepareTable();
      this.checkAllConnections();
    }, () => {
      this.notificationService.openSnackBar("¡Se ha producido un error!");
      this.showProgressBar = false;
    });
  }

  refreshConnections(): void {
    this.checkAllConnections();
  }

  connectionState(scaleId: string | number): ConnectionState {
    return this.connectionByScale[String(scaleId)] || 'pending';
  }

  connectionLabel(scaleId: string | number): string {
    const state = this.connectionState(scaleId);
    if (state === 'online') return 'Conectado';
    if (state === 'offline') return 'Sin conexión';
    return 'Comprobando…';
  }

  private checkAllConnections(): void {
    if (!this.socket?.connected || this.dataSource1.length === 0) {
      return;
    }
    this.checkingConnections = true;
    const scales = [...this.dataSource1];
    let index = 0;

    const checkNext = () => {
      if (index >= scales.length) {
        this.checkingConnections = false;
        return;
      }
      const scale = scales[index];
      const scaleId = String(scale['id']);
      this.connectionByScale[scaleId] = 'pending';
      this.pingScale(scaleId).then((online) => {
        this.connectionByScale[scaleId] = online ? 'online' : 'offline';
        index += 1;
        checkNext();
      });
    };

    checkNext();
  }

  private pingScale(scaleId: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.socket?.connected) {
        resolve(false);
        return;
      }
      let resolved = false;
      const onResponse = (msg: { scale?: string | number; scale_id?: string | number }) => {
        const msgScale = String(msg?.scale ?? msg?.scale_id ?? '');
        if (msgScale !== scaleId) {
          return;
        }
        if (resolved) {
          return;
        }
        resolved = true;
        this.socket?.off('HelloControlResponse', onResponse);
        resolve(true);
      };
      this.socket.on('HelloControlResponse', onResponse);
      this.socket.emit('joinScale', { scale: scaleId });
      this.socket.emit('HelloControl', { msg: 'Hello', scale: scaleId });
      setTimeout(() => {
        if (resolved) {
          return;
        }
        resolved = true;
        this.socket?.off('HelloControlResponse', onResponse);
        resolve(false);
      }, 3000);
    });
  }

  newScale() {
    if (!this.authService.canAccessSocketDiagnostico()) {
      return;
    }

    const dialogRef = this.dialog.open(NewScaleComponent, {
      width: '90%',
      maxWidth: '500px'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.showProgressBar = true;
        this.scalesService.newScale(
          result["name"],
          result["description"],
          result["active"].toString(),
          result["default_id_camion"],
          result["default_id_material"],
        ).subscribe(() => {
          this.notificationService.openSnackBar("Punto de conexión creado correctamente!");
          this.showProgressBar = false;
          this.getScales();
        }, () => {
          this.notificationService.openSnackBar("¡Se ha producido un error!");
          this.showProgressBar = false;
        });
      }
    });
  }

  editarScale(id: number) {
    for (var i = 0; i < this.dataSource1.length; i++) {
      if (this.dataSource1[i]["id"] === id) {
        const dialogRef = this.dialog.open(EditScaleComponent, {
          data: {
            'id': this.dataSource1[i]["id"],
            'name': this.dataSource1[i]["name"],
            'description': this.dataSource1[i]["description"],
            'active': this.dataSource1[i]["active"],
            'default_id_camion': this.dataSource1[i]["default_id_camion"],
            'default_id_material': this.dataSource1[i]["default_id_material"],
            'crop_x': this.dataSource1[i]["crop_x"],
            'crop_y': this.dataSource1[i]["crop_y"],
            'crop_w': this.dataSource1[i]["crop_w"],
            'crop_h': this.dataSource1[i]["crop_h"],
          },
          width: '90%',
          maxWidth: '500px'
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.showProgressBar = true;
            this.scalesService.editScale(
              id,
              result["name"],
              result["description"],
              result["active"].toString(),
              result["default_id_camion"],
              result["default_id_material"],
              result["crop_x"] != null ? { x: Number(result["crop_x"]), y: Number(result["crop_y"]), w: Number(result["crop_w"]), h: Number(result["crop_h"]) } : null,
            ).subscribe(() => {
              this.notificationService.openSnackBar("Punto de conexión modificado correctamente!");
              this.showProgressBar = false;
              this.getScales();
            }, () => {
              this.notificationService.openSnackBar("¡Se ha producido un error!");
              this.showProgressBar = false;
            });
          }
        });
        break;
      }
    }
  }

  deleteScale(id: number) {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Eliminar punto de conexión', body: '¿Seguro que deseas eliminar este punto de conexión?' },
      width: '90%',
      maxWidth: '350px'
    });
    confirmDialog.afterClosed().subscribe(result => {
      if (result["accion"] === 'true') {
        this.showProgressBar = true;
        this.scalesService.deleteScale(id).subscribe(() => {
          this.notificationService.openSnackBar("¡Punto de conexión eliminado correctamente!");
          this.showProgressBar = false;
          this.getScales();
        }, () => {
          this.notificationService.openSnackBar("¡Se ha producido un error!");
          this.showProgressBar = false;
        });
      }
    });
  }

  prepareTable() {
    this.dataSource = new MatTableDataSource(this.dataSource1);
    this.paginator._intl.itemsPerPageLabel = 'Puntos de conexión por página:';
    this.filter = '';
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }
}
